import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2Icon, Loader2Icon } from "lucide-react";
import { Button } from "./Button";
import { profileOptions } from "../data/content";
import { ContactFields, useContactForm } from "../hooks/useContactForm";
const inputBase = "mt-2 block w-full rounded-md border bg-white px-4 py-3 text-ink placeholder:text-ink/40 transition-[border-color,box-shadow] duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-gold/40";
export function ContactForm() {
  const {
    values,
    errors,
    status,
    setField,
    handleSubmit,
    reset
  } = useContactForm();
  const fieldClass = (field: keyof ContactFields) => `${inputBase} ${errors[field] ? "border-red-600 focus:border-red-600" : "border-paper-line focus:border-gold"}`;
  const errorText = (field: keyof ContactFields) => errors[field] ? <p id={`error-${field}`} className="mt-1.5 text-sm text-red-700" data-id="element-73" data-mp="3ph65e4e8sk1b">
        {errors[field]}
      </p> : null;
  const aria = (field: keyof ContactFields) => ({
    id: `field-${field}`,
    "aria-invalid": Boolean(errors[field]),
    "aria-describedby": errors[field] ? `error-${field}` : undefined
  });
  return <div className="rounded-lg border border-gold/60 bg-white p-6 shadow-sm sm:p-10" data-id="element-74" data-mp="352d6af06qy7">
      <AnimatePresence mode="wait" initial={false} data-id="element-75" data-mp="84fajp3i3gf1">
        {status === "success" ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex flex-col items-start py-10" role="status" data-id="element-76" data-mp="36marwlefpaop">
            <CheckCircle2Icon className="h-10 w-10 text-gold-deep" aria-hidden="true" data-id="element-77" data-mp="3kn4z50pqx5ls" />
            <h3 className="mt-6 text-2xl font-semibold text-ink" data-id="element-78" data-mp="vv14kcgo7061">Mensaje enviado</h3>
            <p className="mt-3 max-w-sm leading-relaxed text-ink/75" data-id="element-79" data-mp="zvblul9wxd07">
              Gracias por escribirnos. Nos pondremos en contacto a la brevedad para coordinar la consulta.
            </p>
            <button type="button" onClick={reset} className="mt-8 text-sm font-medium text-gold-deep underline-offset-4 hover:underline" data-id="element-80" data-mp="2rxo0ixoxz697">
              Enviar otro mensaje
            </button>
          </motion.div> : <motion.form key="form" noValidate onSubmit={handleSubmit} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="grid gap-6 sm:grid-cols-2" data-id="element-81" data-mp="2d8ht4mfya9q8">
            <div className="sm:col-span-2" data-id="element-82" data-mp="2i72vosiod5hu">
              <label htmlFor="field-nombre" className="text-sm font-medium text-ink" data-id="element-83" data-mp="2i61fyaqpruts">
                Nombre
              </label>
              <input {...aria("nombre")} type="text" autoComplete="name" value={values.nombre} onChange={e => setField("nombre", e.target.value)} className={fieldClass("nombre")} placeholder="Nombre y apellido" data-id="element-84" data-mp="1pix9e1bz38fw" />
              {errorText("nombre")}
            </div>

            <div data-id="element-85" data-mp="3s3rj2rotehwf">
              <label htmlFor="field-email" className="text-sm font-medium text-ink" data-id="element-86" data-mp="z1ggw98pk88">
                Email
              </label>
              <input {...aria("email")} type="email" autoComplete="email" value={values.email} onChange={e => setField("email", e.target.value)} className={fieldClass("email")} placeholder="nombre@correo.com" data-id="element-87" data-mp="opx73q1ym9dw" />
              {errorText("email")}
            </div>

            <div data-id="element-88" data-mp="2zgnhkwoxklpe">
              <label htmlFor="field-whatsapp" className="text-sm font-medium text-ink" data-id="element-89" data-mp="2zg5cdakaix5c">
                WhatsApp <span className="font-normal text-ink/55" data-id="element-90" data-mp="34gt829jgs25w">(opcional)</span>
              </label>
              <input {...aria("whatsapp")} type="tel" autoComplete="tel" value={values.whatsapp} onChange={e => setField("whatsapp", e.target.value)} className={fieldClass("whatsapp")} placeholder="Con código de país" data-id="element-91" data-mp="3jqgcijjnbr2z" />
              {errorText("whatsapp")}
            </div>

            <div className="sm:col-span-2" data-id="element-92" data-mp="6ilcbxpthmlv">
              <label htmlFor="field-perfil" className="text-sm font-medium text-ink" data-id="element-93" data-mp="2hjkw3256eug">
                Perfil
              </label>
              <select {...aria("perfil")} value={values.perfil} onChange={e => setField("perfil", e.target.value)} className={`${fieldClass("perfil")} ${values.perfil ? "" : "text-ink/45"}`} data-id="element-94" data-mp="3tn1gd1jxiw58">
                <option value="" disabled data-id="element-95" data-mp="2d9ukukwkljwy">
                  Selecciona una opción
                </option>
                {profileOptions.map(option => <option key={option} value={option} className="text-ink" data-id="element-96" data-mp="237h4d264v8aj">
                    {option}
                  </option>)}
              </select>
              {errorText("perfil")}
            </div>

            <div className="sm:col-span-2" data-id="element-97" data-mp="2huk299ydbiye">
              <label htmlFor="field-mensaje" className="text-sm font-medium text-ink" data-id="element-98" data-mp="2hvlo89gx9puw">
                Mensaje
              </label>
              <textarea {...aria("mensaje")} rows={5} value={values.mensaje} onChange={e => setField("mensaje", e.target.value)} className={`${fieldClass("mensaje")} resize-y`} placeholder="Cuéntanos sobre tu carrera o tu consulta" data-id="element-99" data-mp="2ly8bdx2nemlr" />
              {errorText("mensaje")}
            </div>

            <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-100" data-mp="11idf7w0qb9rl">
              {status === "error" ? <p className="text-sm text-red-700" role="alert" data-id="element-101" data-mp="ydw7gs383wz6">
                  No pudimos enviar el mensaje. Inténtalo nuevamente.
                </p> : <p className="text-sm text-ink/55" data-id="element-102" data-mp="18clq4n2y1lgh">Respondemos dentro de las 48 horas hábiles.</p>}
              <Button type="submit" disabled={status === "loading"} className="focus-visible:ring-offset-white" data-id="element-103" data-mp="13d92w185ucdd">
                {status === "loading" ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-104" data-mp="1cegs7tsp82vb" />
                    Enviando…
                  </> : "Enviar mensaje"}
              </Button>
            </div>
          </motion.form>}
      </AnimatePresence>
    </div>;
}